import {
  Typography,
  Tag,
  Button,
  Input,
  Select,
  Progress,
  Badge,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  FireOutlined,
  CalendarOutlined,
  SearchOutlined,
  RightOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { actionItemApi } from '@/api/actionItemApi';
import { useActionItemStore } from '@/store/actionItemStore';
import DeadlineCountdown from '@/components/common/DeadlineCountdown';
import { daysUntil } from '@/utils/dateFormatters';
import PageHeader from '@/components/ui/PageHeader';
import EmptyState from '@/components/ui/EmptyState';
import type { ActionItem } from '@/api/actionItemApi';

const { Text } = Typography;

// Quick tab filters rendered as pill chips in the sticky toolbar.
type TabFilter = 'all' | 'open' | 'inReview' | 'done';
const TAB_CHIPS: { value: TabFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'inReview', label: 'In Review' },
  { value: 'done', label: 'Done' },
];

const phaseColors: Record<string, string> = {
  PHASE_A: 'purple',
  PHASE_B: 'blue',
  PHASE_C: 'cyan',
  PHASE_D: 'green',
  PHASE_E: 'gold',
  PHASE_F: 'magenta',
};

const phaseLabels: Record<string, string> = {
  PHASE_A: 'Gate A',
  PHASE_B: 'Gate B',
  PHASE_C: 'Gate C',
  PHASE_D: 'Gate D',
  PHASE_E: 'Gate E',
  PHASE_F: 'Gate F',
};

// --- Helpers ---

function isOverdue(item: ActionItem): boolean {
  if (!item.phaseDeadline) return false;
  return daysUntil(item.phaseDeadline) < 0;
}

function isDueToday(item: ActionItem): boolean {
  if (!item.phaseDeadline) return false;
  return daysUntil(item.phaseDeadline) === 0;
}

function isThisWeek(item: ActionItem): boolean {
  if (!item.phaseDeadline) return false;
  const diff = daysUntil(item.phaseDeadline);
  return diff > 0 && diff <= 7;
}

function getReadiness(item: ActionItem): number {
  if (!item.totalRequirements || item.totalRequirements === 0) return 100;
  return Math.round(
    ((item.totalRequirements - item.unmetRequirements.length) /
      item.totalRequirements) *
      100,
  );
}

// --- Stat Card ---

function StatCard({
  icon,
  color,
  bgColor,
  label,
  count,
}: {
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  label: string;
  count: number;
}) {
  return (
    <div
      className="ui-surface"
      style={{
        flex: 1,
        minWidth: 160,
        padding: '18px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          color,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.02em', color: 'var(--color-text-primary)' }}>
          {count}
        </div>
        <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>{label}</div>
      </div>
    </div>
  );
}

// --- Section Header ---

function SectionHeader({
  label,
  color,
  count,
}: {
  label: string;
  color: string;
  count: number;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 16px',
        borderLeft: `4px solid ${color}`,
        background: `${color}08`,
        borderRadius: 6,
        marginBottom: 8,
      }}
    >
      <Text strong style={{ fontSize: 14, color }}>
        {label}
      </Text>
      <Badge
        count={count}
        style={{ backgroundColor: color }}
        overflowCount={999}
      />
    </div>
  );
}

// --- Item Row ---

function ItemRow({
  item,
  onClick,
}: {
  item: ActionItem;
  onClick: () => void;
}) {
  const readiness = getReadiness(item);

  // Priority tag based on deadline
  let priorityTag: React.ReactNode = null;
  if (item.phaseDeadline) {
    const diff = daysUntil(item.phaseDeadline);
    if (diff < 0) {
      priorityTag = <Tag color="red">Urgent</Tag>;
    } else if (diff <= 3) {
      priorityTag = <Tag color="orange">High</Tag>;
    } else if (diff <= 7) {
      priorityTag = <Tag color="gold">Medium</Tag>;
    }
  }

  // Status tag
  let statusTag: React.ReactNode;
  if (item.currentPhase === 'PHASE_E') {
    statusTag = <Tag color="purple">In Review</Tag>;
  } else if (item.unmetRequirements.length > 0) {
    statusTag = <Tag color="blue">In Progress</Tag>;
  } else {
    statusTag = <Tag color="green">Ready</Tag>;
  }

  // Phase tag
  const phaseTag = (
    <Tag color={phaseColors[item.currentPhase] || 'default'}>
      {phaseLabels[item.currentPhase] || item.currentPhase}
    </Tag>
  );

  // Bottom line: prefer the backend's action summary, else the first unmet
  // requirement description.
  const bottomSummary =
    item.actionSummary ||
    (item.unmetRequirements.length > 0
      ? item.unmetRequirements[0].label
      : 'All requirements met');

  return (
    <div
      onClick={onClick}
      className="ui-surface ui-surface--interactive"
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '14px 16px',
        marginBottom: 8,
        borderRadius: 12,
      }}
    >
      {/* Left icon */}
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 8,
          background: 'var(--color-bg-muted)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: 14,
          flexShrink: 0,
          color: 'var(--color-text-tertiary)',
          fontSize: 16,
        }}
      >
        <FileTextOutlined />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Top line: title + tags */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
            marginBottom: 4,
          }}
        >
          <Text
            strong
            style={{ fontSize: 14, marginRight: 4 }}
            ellipsis={{ tooltip: item.title }}
          >
            {item.title}
          </Text>
          {priorityTag}
          {statusTag}
          {phaseTag}
        </div>
        {/* Bottom line: agency + action summary (or first unmet requirement) */}
        <Text type="secondary" style={{ fontSize: 12 }}>
          {item.agencyName || 'No agency'}
          {' \u00B7 '}
          {bottomSummary}
        </Text>
        {/* Unmet requirements preview (up to 4) */}
        {item.unmetRequirements.length > 0 && (
          <ul
            style={{
              listStyle: 'none',
              margin: '6px 0 0',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {item.unmetRequirements.slice(0, 4).map((req) => (
              <li
                key={req.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 11,
                  color: 'var(--color-text-tertiary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <span
                  style={{
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    background: 'var(--color-text-disabled)',
                    flexShrink: 0,
                  }}
                />
                {req.label}
              </li>
            ))}
            {item.unmetRequirements.length > 4 && (
              <li style={{ fontSize: 11, color: 'var(--color-text-disabled)', paddingLeft: 10 }}>
                +{item.unmetRequirements.length - 4} more
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Right side: readiness + deadline + complete button + arrow */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexShrink: 0,
          marginLeft: 16,
        }}
      >
        {/* Readiness circle */}
        <Progress
          type="circle"
          percent={readiness}
          size={36}
          strokeColor={
            readiness === 100 ? '#52c41a' : readiness >= 60 ? '#1677ff' : '#faad14'
          }
          format={(pct) => (
            <span style={{ fontSize: 10, fontWeight: 600 }}>{pct}%</span>
          )}
        />

        {/* Deadline */}
        <div style={{ minWidth: 70, textAlign: 'center' }}>
          {item.phaseDeadline ? (
            <DeadlineCountdown deadline={item.phaseDeadline} compact />
          ) : (
            <Text type="secondary" style={{ fontSize: 11 }}>
              No deadline
            </Text>
          )}
        </div>

        {/* Complete button — there is no completion endpoint; the work is
            resolved on the opportunity. Guide the user there. */}
        <Button
          type="default"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            message.info(
              `Open ${item.title} to complete: ${item.actionSummary || 'this task'}`,
            );
            onClick();
          }}
          style={{
            borderColor: 'var(--color-success)',
            color: 'var(--color-success)',
            fontWeight: 500,
            fontSize: 12,
          }}
        >
          Complete
        </Button>

        {/* Arrow */}
        <RightOutlined style={{ color: 'var(--color-text-tertiary)', fontSize: 12 }} />
      </div>
    </div>
  );
}

// --- Main Page ---

const ActionItemsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [tabFilter, setTabFilter] = useState<TabFilter>('all');
  const [phaseFilter, setPhaseFilter] = useState<string>('all');
  const setStoreCount = useActionItemStore((s) => s.fetchCount);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const items = await actionItemApi.getAll();
      setData(items);
      setStoreCount();
    } catch (error) {
      console.error('Failed to fetch action items:', error);
    } finally {
      setLoading(false);
    }
  }, [setStoreCount]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Stat counts ---
  const stats = useMemo(() => {
    const overdueCount = data.filter(isOverdue).length;
    const dueTodayCount = data.filter(isDueToday).length;
    const thisWeekCount = data.filter(isThisWeek).length;
    return {
      total: data.length,
      overdue: overdueCount,
      dueToday: dueTodayCount,
      thisWeek: thisWeekCount,
    };
  }, [data]);

  // --- Filtered data ---
  const filteredData = useMemo(() => {
    let items = data;

    // Search filter
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          (item.agencyName && item.agencyName.toLowerCase().includes(q)),
      );
    }

    // Tab filter
    if (tabFilter === 'open') {
      items = items.filter(
        (item) =>
          item.unmetRequirements.length > 0 && item.currentPhase !== 'PHASE_E',
      );
    } else if (tabFilter === 'inReview') {
      items = items.filter((item) => item.currentPhase === 'PHASE_E');
    } else if (tabFilter === 'done') {
      items = items.filter((item) => item.unmetRequirements.length === 0);
    }

    // Phase filter
    if (phaseFilter !== 'all') {
      items = items.filter((item) => item.currentPhase === phaseFilter);
    }

    return items;
  }, [data, search, tabFilter, phaseFilter]);

  // --- Grouped sections ---
  const grouped = useMemo(() => {
    const overdue: ActionItem[] = [];
    const thisWeek: ActionItem[] = [];
    const upcoming: ActionItem[] = [];

    for (const item of filteredData) {
      if (isOverdue(item)) {
        overdue.push(item);
      } else if (item.phaseDeadline && daysUntil(item.phaseDeadline) <= 7) {
        thisWeek.push(item);
      } else {
        upcoming.push(item);
      }
    }

    return { overdue, thisWeek, upcoming };
  }, [filteredData]);

  const noResults = filteredData.length === 0;

  return (
    <div>
      {/* Page header */}
      <PageHeader
        title="My Action Items"
        subtitle={
          loading
            ? 'Loading…'
            : `${stats.total} open · ${stats.overdue} overdue · ${stats.dueToday} due today`
        }
      />

      {/* Summary stat cards */}
      <div
        className="ui-stagger"
        style={{
          display: 'flex',
          gap: 14,
          marginBottom: 20,
          flexWrap: 'wrap',
        }}
      >
        <StatCard
          icon={<ClockCircleOutlined />}
          color="var(--color-primary)"
          bgColor="var(--color-primary-light)"
          label="Open Tasks"
          count={stats.total}
        />
        <StatCard
          icon={<FireOutlined />}
          color="var(--color-error)"
          bgColor="var(--color-error-light)"
          label="Overdue"
          count={stats.overdue}
        />
        <StatCard
          icon={<CalendarOutlined />}
          color="var(--color-warning)"
          bgColor="var(--color-warning-light)"
          label="Due Today"
          count={stats.dueToday}
        />
        <StatCard
          icon={<CalendarOutlined />}
          color="var(--color-primary)"
          bgColor="var(--color-primary-light)"
          label="This Week"
          count={stats.thisWeek}
        />
      </div>

      {/* Search + Filter bar */}
      <div className="ui-toolbar">
        <Input
          placeholder="Search by title or agency..."
          prefix={<SearchOutlined style={{ color: 'var(--color-text-tertiary)' }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ maxWidth: 280 }}
        />

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {TAB_CHIPS.map((chip) => (
            <button
              key={chip.value}
              type="button"
              className="ui-chip ui-focusable"
              data-active={tabFilter === chip.value}
              onClick={() => setTabFilter(chip.value)}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1 }} />

        <Select
          value={phaseFilter}
          onChange={setPhaseFilter}
          style={{ width: 140 }}
          options={[
            { label: 'All phases', value: 'all' },
            { label: 'Gate A', value: 'PHASE_A' },
            { label: 'Gate B', value: 'PHASE_B' },
            { label: 'Gate C', value: 'PHASE_C' },
            { label: 'Gate D', value: 'PHASE_D' },
            { label: 'Gate E', value: 'PHASE_E' },
            { label: 'Gate F', value: 'PHASE_F' },
          ]}
        />
      </div>

      {/* Loading state — shimmer skeleton rows */}
      {loading && (
        <div className="ui-stagger" style={{ marginTop: 4 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="ui-surface"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '14px 16px',
                marginBottom: 8,
                borderRadius: 12,
              }}
            >
              <div className="ui-skeleton" style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div className="ui-skeleton" style={{ width: '40%', height: 14, marginBottom: 8 }} />
                <div className="ui-skeleton" style={{ width: '65%', height: 12 }} />
              </div>
              <div className="ui-skeleton" style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }} />
            </div>
          ))}
        </div>
      )}

      {/* Grouped sections */}
      {!loading && (
        <>
          {/* OVERDUE section */}
          {grouped.overdue.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <SectionHeader
                label="OVERDUE"
                color="#ff4d4f"
                count={grouped.overdue.length}
              />
              <div className="ui-stagger">
                {grouped.overdue.map((item) => (
                  <ItemRow
                    key={item.opportunityId}
                    item={item}
                    onClick={() =>
                      navigate(`/opportunities/${item.opportunityId}`)
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* THIS WEEK section */}
          {grouped.thisWeek.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <SectionHeader
                label="THIS WEEK"
                color="#fa8c16"
                count={grouped.thisWeek.length}
              />
              <div className="ui-stagger">
                {grouped.thisWeek.map((item) => (
                  <ItemRow
                    key={item.opportunityId}
                    item={item}
                    onClick={() =>
                      navigate(`/opportunities/${item.opportunityId}`)
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* UPCOMING section */}
          {grouped.upcoming.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <SectionHeader
                label="UPCOMING"
                color="#1677ff"
                count={grouped.upcoming.length}
              />
              <div className="ui-stagger">
                {grouped.upcoming.map((item) => (
                  <ItemRow
                    key={item.opportunityId}
                    item={item}
                    onClick={() =>
                      navigate(`/opportunities/${item.opportunityId}`)
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {noResults && (
            <EmptyState
              icon={<CheckCircleOutlined />}
              title={
                data.length === 0
                  ? 'All caught up'
                  : 'No matching items'
              }
              description={
                data.length === 0
                  ? 'You have no pending action items right now.'
                  : 'Try clearing your search or adjusting the filters above.'
              }
            />
          )}
        </>
      )}
    </div>
  );
};

export default ActionItemsPage;
